import { PanelLeftIcon, PanelRightIcon } from 'lucide-react';
import {
  type ComponentProps,
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from '../../common/hooks/merge-refs';
import { badgeVariants } from '../badge/badge-variants';
import { Button, type ButtonProps } from '../button/button';
import { Kbd } from '../kbd/kbd';
import { formatShortcut } from '../rich-text/shortcuts';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip/tooltip';
import { useSidebar } from './sidebar-context';
import './sidebar.css';
import { cn } from '../../common/functions/cn';

const SIDEBAR_RESIZE_STEP = 16;

/** Whether a click on a link moves this page, rather than opening its destination somewhere else. */
const movesThisPage = (event: MouseEvent) => {
  const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
  return (
    !!link &&
    event.button === 0 &&
    !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) &&
    (!link.target || link.target === '_self')
  );
};

const Sidebar = ({
  className,
  style,
  children,
  ref,
  ...props
}: HTMLAttributes<HTMLElement> & {
  ref?: Ref<HTMLElement>;
}) => {
  const { state, side, isDrawer, sidebarRef, sidebarId, initialWidth } = useSidebar();

  const asideRef = useMemo(() => mergeRefs(sidebarRef, ref), [sidebarRef, ref]);

  // A drawer floats over the page any link inside it navigates to, so it has to take itself away.
  // A modified click opens that destination elsewhere and leaves this page, and its drawer, alone.
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (movesThisPage(event) && sidebar.matches(':popover-open')) {
        sidebar.hidePopover();
      }
    };

    sidebar.addEventListener('click', handleClick);
    return () => sidebar.removeEventListener('click', handleClick);
  }, [sidebarRef]);

  return (
    <aside
      popover="auto"
      data-slot="sidebar"
      data-state={state}
      data-side={side}
      data-mode={isDrawer ? 'drawer' : 'rail'}
      ref={asideRef}
      className={cn('group shrink-0 text-sidebar-foreground', className)}
      style={{ ...style, ...(initialWidth && ({ '--sidebar-width': `${initialWidth}px` } as CSSProperties)) }}
      {...props}
      id={sidebarId}
    >
      <div data-slot="sidebar-inner" className="flex size-full flex-col bg-sidebar">
        {children}
      </div>
    </aside>
  );
};

interface SidebarTriggerProps extends Omit<ButtonProps, 'children'> {
  /**
   * What the trigger says in each state, for a panel the page calls something other than a
   * sidebar. Given one, the trigger writes the label out beside the icon while the panel is open,
   * where the collapsed rail has only the tooltip.
   */
  label?: { collapse: string; expand: string };
}

const SidebarTrigger = ({ className, onClick, label, ref, ...props }: SidebarTriggerProps) => {
  const { open, side, isDrawer, keyboardShortcut, sidebarId, toggleSidebar } = useSidebar();
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mergedRef = useMemo(() => mergeRefs(triggerRef, ref), [ref]);
  const [inPanel, setInPanel] = useState(false);

  const text = isDrawer
    ? t(($) => $.toggleSidebar)
    : open
      ? (label?.collapse ?? t(($) => $.collapseSidebar))
      : (label?.expand ?? t(($) => $.expandSidebar));
  const showLabel = Boolean(label) && open && !isDrawer;

  const trigger = (
    <Button
      ref={mergedRef}
      data-slot="sidebar-trigger"
      variant="ghost"
      size={showLabel ? 'default' : 'icon-sm'}
      popoverTarget={isDrawer ? sidebarId : undefined}
      aria-expanded={isDrawer ? undefined : open}
      className={cn(
        'aria-expanded:bg-transparent aria-expanded:text-current',
        'aria-expanded:hover:bg-muted aria-expanded:hover:text-foreground',
        showLabel && 'w-full justify-start gap-2 px-2 font-normal',
        className
      )}
      onClick={(event) => {
        onClick?.(event);
        if (!isDrawer && !event.defaultPrevented) {
          toggleSidebar();
        }
      }}
      {...props}
    >
      {side === 'right' ? <PanelRightIcon /> : <PanelLeftIcon />}
      <span className={showLabel ? 'truncate' : 'sr-only'}>{text}</span>
    </Button>
  );

  return (
    <Tooltip
      onOpenChange={(tooltipOpen) => {
        if (tooltipOpen) {
          setInPanel(Boolean(triggerRef.current?.closest('[data-slot="sidebar"]')));
        }
      }}
    >
      <TooltipTrigger render={trigger} />
      <TooltipContent
        side={inPanel ? (side === 'right' ? 'left' : 'right') : 'bottom'}
        className="flex"
        hidden={showLabel}
      >
        {text}
        {keyboardShortcut && <Kbd>{formatShortcut(keyboardShortcut)}</Kbd>}
      </TooltipContent>
    </Tooltip>
  );
};

interface SidebarRailProps extends ComponentProps<'button'> {
  /** What the rail announces in each state, for a panel the page calls something other than a sidebar. */
  label?: { collapse: string; expand: string };
}

/** A drag handle for the panel's width, and its toggle on a double click, the way a splitter is. */
const SidebarRail = ({
  className,
  label,
  onClick,
  onDoubleClick,
  onPointerDown,
  onKeyDown,
  ...props
}: SidebarRailProps) => {
  const { open, side, isDrawer, toggleSidebar, startResize, resizeBy } = useSidebar();
  const { t } = useTranslation();
  const text = open ? (label?.collapse ?? t(($) => $.collapseSidebar)) : (label?.expand ?? t(($) => $.expandSidebar));
  const hint = open ? t(($) => $.doubleClickToCollapse) : t(($) => $.doubleClickToExpand);

  // A drawer has no edge to drag, and the trigger is the only control it needs.
  if (isDrawer) {
    return null;
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event);
    startResize(event);
  };

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    onClick?.(event);

    // Every drag ends in a click on the rail, so the pointer is left to the double click below and
    // a single one does nothing. A keyboard activation reports no click count and no double click
    // to wait for, which is the whole of the rail still being a button.
    if (event.detail === 0) {
      toggleSidebar();
    }
  };

  const handleDoubleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    onDoubleClick?.(event);
    toggleSidebar();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (!open || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return;
    }

    event.preventDefault();
    // The rail is on the panel's outer edge, so on the right it is the left arrow that widens.
    const widens = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    resizeBy(event.key === widens ? SIDEBAR_RESIZE_STEP : -SIDEBAR_RESIZE_STEP);
  };

  return (
    <button
      type="button"
      data-slot="sidebar-rail"
      aria-label={text}
      title={hint}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'absolute inset-y-0 z-20 flex w-4 cursor-ew-resize touch-none outline-hidden',
        side === 'right' ? '-left-4 translate-x-1/2' : '-right-4 -translate-x-1/2',
        'after:absolute after:inset-y-0 after:left-1/2 after:w-0.5',
        'after:transition-colors after:delay-0 after:duration-150',
        'hover:after:bg-primary hover:after:delay-300',
        'focus-visible:after:bg-primary focus-visible:after:delay-0',
        className
      )}
      {...props}
    />
  );
};

const SidebarInset = ({ className, ...props }: ComponentProps<'main'>) => {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn('relative flex w-full flex-1 flex-col bg-background', className)}
      {...props}
    />
  );
};

const SidebarHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="sidebar-header" className={cn('flex flex-col gap-2 p-2', className)} {...props} />;
};

const SidebarFooter = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="sidebar-footer" className={cn('flex flex-col gap-2 p-2', className)} {...props} />;
};

const SidebarContent = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="sidebar-content"
      className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)}
      {...props}
    />
  );
};

const SidebarGroup = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div data-slot="sidebar-group" className={cn('relative flex w-full min-w-0 flex-col p-2', className)} {...props} />
  );
};

const SidebarGroupLabel = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="sidebar-group-label"
      className={cn(
        'flex h-8 shrink-0 items-center overflow-hidden whitespace-nowrap rounded-sm px-2 font-medium text-muted-foreground text-xs',
        'outline-hidden ring-sidebar-ring focus-visible:ring-2',
        'group-data-[state=collapsed]:-mt-8 group-data-[state=collapsed]:opacity-0',
        '[&>svg]:size-4 [&>svg]:shrink-0',
        className
      )}
      {...props}
    />
  );
};

const SidebarMenu = ({ className, ...props }: ComponentProps<'ul'>) => {
  return <ul data-slot="sidebar-menu" className={cn('flex w-full min-w-0 flex-col gap-0.5', className)} {...props} />;
};

const SidebarMenuItem = ({ className, ...props }: ComponentProps<'li'>) => {
  return <li data-slot="sidebar-menu-item" className={cn('group/menu-item relative', className)} {...props} />;
};

const sidebarMenuButtonClasses = cn(
  'peer/menu-button group/menu-button relative flex h-9 w-full cursor-pointer items-center gap-2 overflow-hidden whitespace-nowrap rounded-sm px-2 text-left text-sm',
  'outline-hidden ring-sidebar-ring focus-visible:z-10 focus-visible:ring-2',
  'hover:bg-muted',
  'data-active:bg-accent data-active:font-medium data-active:text-accent-foreground',
  'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  'data-active:before:absolute data-active:before:inset-y-2 data-active:before:left-0 data-active:before:w-0.5 data-active:before:rounded-sm data-active:before:bg-primary',
  'group-has-[[data-slot=sidebar-menu-action]]/menu-item:pr-8',
  'not-data-active:group-has-[[data-slot=sidebar-menu-action]:hover]/menu-item:bg-muted',
  'group-data-[state=collapsed]:h-8 group-data-[state=collapsed]:pr-0 group-data-[state=collapsed]:pl-(--sidebar-collapsed-row-padding)',
  'group-data-[state=collapsed]:[&>span:not([data-slot])]:sr-only',
  "[&>span:not([data-slot])]:truncate [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
);

interface SidebarMenuRowProps {
  isActive?: boolean;
  tooltip?: string | ComponentProps<typeof TooltipContent>;
}

/** The tooltip stands in for the label a collapsed rail cannot show, and a drawer never reports collapsed. */
const SidebarMenuTooltip = ({
  row,
  tooltip,
}: {
  row: ReactElement;
  tooltip: string | ComponentProps<typeof TooltipContent>;
}) => {
  const { state, side } = useSidebar();
  const content = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipContent
        side={side === 'right' ? 'left' : 'right'}
        align="center"
        {...content}
        hidden={state !== 'collapsed' || content.hidden}
      />
    </Tooltip>
  );
};

/** A menu row that acts on the page it is already on. */
const SidebarMenuButton = ({
  isActive,
  tooltip,
  className,
  ...props
}: ComponentProps<'button'> & SidebarMenuRowProps) => {
  const row = (
    <button
      type="button"
      data-slot="sidebar-menu-button"
      data-active={isActive ? '' : undefined}
      aria-current={isActive || undefined}
      className={cn(sidebarMenuButtonClasses, className)}
      {...props}
    />
  );

  return tooltip ? <SidebarMenuTooltip row={row} tooltip={tooltip} /> : row;
};

/** A menu row that navigates on its own, styled as one of the buttons beside it. */
const SidebarMenuLink = ({
  isActive,
  tooltip,
  disabled,
  href,
  className,
  ...props
}: ComponentProps<'a'> & SidebarMenuRowProps & { disabled?: boolean }) => {
  const row = (
    <a
      data-slot="sidebar-menu-button"
      data-active={isActive ? '' : undefined}
      // Without an href the row is neither focusable nor navigable, which is the whole of disabled.
      href={disabled ? undefined : href}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      className={cn(sidebarMenuButtonClasses, className)}
      {...props}
    />
  );

  return tooltip ? <SidebarMenuTooltip row={row} tooltip={tooltip} /> : row;
};

/**
 * A control of its own on a menu row, such as a close button. It is the row's sibling rather than
 * its child: the row is already a button, and a button cannot hold another. Collapsed there is no
 * room for it beside the icon, and the row's tooltip is the only label left, so it goes away.
 */
const SidebarMenuAction = ({ className, ...props }: ComponentProps<'button'>) => {
  return (
    <button
      type="button"
      data-slot="sidebar-menu-action"
      className={cn(
        'absolute top-1 right-1 flex size-7 items-center justify-center rounded-sm',
        'outline-hidden ring-sidebar-ring focus-visible:ring-2',
        'peer-data-active/menu-button:text-accent-foreground',
        'hover:bg-foreground/8',
        'group-data-[state=collapsed]:hidden',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    />
  );
};

/**
 * The header of a `<details>` group, styled as a menu row. Opening and closing the group is the
 * browser's, so the group needs no state, no handler and no aria of its own.
 */
const SidebarMenuSummary = ({
  isActive,
  tooltip,
  className,
  ...props
}: ComponentProps<'summary'> & SidebarMenuRowProps) => {
  const row = (
    <summary
      data-slot="sidebar-menu-button"
      data-active={isActive ? '' : undefined}
      aria-current={isActive || undefined}
      className={cn(sidebarMenuButtonClasses, className)}
      {...props}
    />
  );

  return tooltip ? <SidebarMenuTooltip row={row} tooltip={tooltip} /> : row;
};

const SidebarMenuBadge = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="sidebar-menu-badge"
      className={cn(
        badgeVariants({ size: 'sm' }),
        'ml-auto',
        'group-data-[state=collapsed]:absolute group-data-[state=collapsed]:top-0',
        'group-data-[state=collapsed]:left-[calc(var(--sidebar-width-collapsed)-var(--spacing)*4-1px)] group-data-[state=collapsed]:-translate-x-full',
        'group-data-[side=right]:group-data-[state=collapsed]:right-[calc(var(--sidebar-width-collapsed)-var(--spacing)*4-1px)]',
        'group-data-[side=right]:group-data-[state=collapsed]:left-auto group-data-[side=right]:group-data-[state=collapsed]:translate-x-full',
        className
      )}
      {...props}
    />
  );
};

const SidebarMenuSub = ({ className, ...props }: ComponentProps<'ul'>) => {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col border-sidebar-border border-l px-2.5 py-0.5',
        'group-data-[state=collapsed]:hidden',
        className
      )}
      {...props}
    />
  );
};

const SidebarMenuSubItem = ({ className, ...props }: ComponentProps<'li'>) => {
  return <li data-slot="sidebar-menu-sub-item" className={cn('group/menu-sub-item relative', className)} {...props} />;
};

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLink,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSummary,
  SidebarRail,
  SidebarTrigger,
};
